import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { type DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import '@adornis/file-utils/client/x-multiple-image-picker.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { type ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { g_getProductCategoryString } from '@chancenlogistik/client/helper.js';
import {
  AdvertisingMaterial,
  ChancenBrief,
  ChancenBriefVariable,
  DocxVariableType,
  EventTicket,
  Furnishing,
  LearningMaterial,
  LogistikProduct,
  LogistikProductSet,
  TravelCertificate,
  WorkMaterial,
  WorkshopPack,
  possiblePrefillChancenBriefVariableNames,
  type LogistikProductUnion,
} from '@chancenwerk/db/LogistikProduct.js';
import {
  LogistikAdvertisingMaterialCategory,
  LogistikLearningMaterialCategory,
  LogistikProductAvailability,
  LogistikSchoolGrade,
  Services
} from '@chancenwerk/db/enums/LogistikEnums.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-date-picker-dropdown.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-multiselection.js';
import '@chancenwerk/design/components/cr-dropdown-selection.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-image.js';
import '@chancenwerk/design/components/cr-input.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/cr-tooltip.js';
import '@chancenwerk/design/components/fonts.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { of, switchMap, takeUntil } from 'rxjs';
import '../logistik-component-delete-button.js';
import '../logistik-component-target-audience-selector.js';
import '../logistik-component-tinymce.js';
import './../logistik-component-product-set-item-selector.js';

/**
 * @customElement logistik-dialog-edit-product
 */
@customElement('logistik-dialog-edit-product')
export class LogistikDialogEditProduct extends ChromiumDialog<void> {
  static override get element_name() {
    return 'logistik-dialog-edit-product';
  }

  @property({ attribute: false }) productID = new RXController<Maybe<string>>(this, undefined);
  @property({ attribute: false }) public product = new RXController(
    this,
    new LogistikProduct({}) as LogistikProductUnion,
  );

  @state() private readonly _productDirty = new RXController(
    this,
    this.product.observable.pipe(switchMap(event => (event.dirty ? event.dirty : of(false)))),
  );
  @state() private readonly _errorMsg = new RXController(this, '');

  @state() private readonly _pdfID = new RXController<string | null>(this, null);

  @state() private readonly _pdfFile = new RXController(
    this,
    this._pdfID.observable.pipe(
      switchMap(id => (id ? AdornisFile.getByID<AdornisFile>(id)(AdornisFile.allFields) : of(null))),
    ),
  );

  @state() private readonly _selectedProductType = new RXController(
    this,
    new LogistikProduct({}) as LogistikProductUnion,
  );

  @state() private readonly _isUploadingPdf = new RXController(this, false);

  private readonly _form = new FormController(this, this._selectedProductType.observable, {
    success: logistikProduct => this._handleFormSuccess(logistikProduct),
    error: err => this._handleFormError(err),
  });

  private async _handleFormSuccess(logistikProduct: LogistikProduct) {
    if (!logistikProduct.imageIDs || logistikProduct.imageIDs.length === 0)
      return this._errorMsg.next('Bitte füge min. ein Bild hinzu!');
    const firstImageID = logistikProduct.imageIDs.at(0);
    if (!firstImageID) return this._errorMsg.next('Bitte füge min. ein Bild hinzu!');
    logistikProduct.imageID = firstImageID;
    if (
      logistikProduct instanceof LearningMaterial &&
      (logistikProduct.description === '' || logistikProduct.description === null)
    )
      return this._errorMsg.next('Bitte füge eine Beschreibung hinzu!');

    let success = false;
    if (logistikProduct instanceof LearningMaterial) {
      success = await this._handleLearningMaterialSuccess(logistikProduct);
    } else if (logistikProduct instanceof AdvertisingMaterial) {
      success = await this._handleAdvertisingMaterialSuccess(logistikProduct);
    } else {
      success = await this._handleLogistikProductSuccess(logistikProduct);
    }
    if (success) {
      void ChromiumSnackbar.show('Produkt gespeichert');
      void this.close();
    }
  }

  private async _handleLearningMaterialSuccess(learningMaterial: LearningMaterial): Promise<boolean> {
    if (this._pdfID.value) learningMaterial.pdfID = this._pdfID.value;
    else {
      this._errorMsg.next('Bitte füge ein PDF hinzu!');
      return false;
    }
    if (!learningMaterial._id)
      return await learningMaterial
        .create()
        .then(v => v.length > 0)
        .catch(e => {
          this._errorMsg.next(e.detail as string);
          return false;
        });
    else
      return await learningMaterial
        .save()
        .then(v => v > 0)
        .catch(e => {
          this._errorMsg.next(e.detail as string);
          return false;
        });
  }

  private async _handleAdvertisingMaterialSuccess(advertisingMaterial: AdvertisingMaterial): Promise<boolean> {
    if (this._pdfID.value) advertisingMaterial.pdfID = this._pdfID.value;
    if (!advertisingMaterial._id)
      return await advertisingMaterial
        .create()
        .then(v => v.length > 0)
        .catch(e => {
          this._errorMsg.next(e.detail as string);
          return false;
        });
    else
      return await advertisingMaterial
        .save()
        .then(v => v > 0)
        .catch(e => {
          this._errorMsg.next(e.detail as string);
          return false;
        });
  }

  private async _handleLogistikProductSuccess(logistikProduct: LogistikProduct): Promise<boolean> {
    if (!logistikProduct._id)
      return await logistikProduct
        .create()
        .then(v => v.length > 0)
        .catch(e => {
          this._errorMsg.next(e.detail as string);
          return false;
        });
    else
      return await logistikProduct
        .save()
        .then(v => v > 0)
        .catch(e => {
          this._errorMsg.next(e.detail as string);
          return false;
        });
  }

  private _handleFormError(err: ValidationError<any>) {}

  override connectedCallback() {
    super.connectedCallback();
    this.product.observable.pipe(takeUntil(this.disconnected)).subscribe(e => {
      const product = g_getProductCategoryString(e._class);
      this.heading = !e._id ? `${product} erstellen` : `${product} bearbeiten`;

      if (e._id) {
        this._selectedProductType.next(e);
        this._pdfID.next(e.pdfID);
        return;
      }

      const productClass = {
        [LearningMaterial._class]: LearningMaterial,
        [AdvertisingMaterial._class]: AdvertisingMaterial,
        [WorkMaterial._class]: WorkMaterial,
        [EventTicket._class]: EventTicket,
        [Furnishing._class]: Furnishing,
        [ChancenBrief._class]: ChancenBrief,
        [WorkshopPack._class]: WorkshopPack,
        [LogistikProductSet._class]: LogistikProductSet,
        [TravelCertificate._class]: TravelCertificate,
      }[e._class];

      if (!productClass) return;
      this._selectedProductType.next(new productClass({}));
      e.pdfID && this._pdfID.next(e.pdfID);
    });

    this.productID.observable.pipe(takeUntil(this.disconnected)).subscribe(async productID => {
      if (!productID) return;
      const product = await LogistikProduct.getByID<LogistikProduct>(productID)(LogistikProduct.allFields);
      if (product) {
        if (!product.imageIDs) product.imageIDs = [product.imageID];
        this.product.next(product as LearningMaterial | AdvertisingMaterial);
      }
    });
  }

  public override content() {
    if (!this.product.value) return nothing;
    return html`
      <cr-flex space="md" padding="md">
        ${this._errorMsg.value.length
          ? html`<cr-text ${css({ color: 'red' })}>${this._errorMsg.value}</cr-text>`
          : nothing}
        ${this._basicInformationInputFields()} ${this._productSpecificInputFields()}
        <cr-input flex placeholder="Hinweis vor Bestellung" ${this._form.field('notice')}></cr-input>
        <x-hr ${css({ background: '#e9e9e9' })}></x-hr>
        <logistik-component-tinymce
          minheight="10vh"
          ${this._form.field('description')}
          placeholder="Beschreibung"
        ></logistik-component-tinymce>
        <x-hr ${css({ background: '#e9e9e9' })}></x-hr>
        <x-flex horizontal space="md" wrap>
          <x-flex space="sm" flex> ${this._getFilePreview()} ${this._getPDFUploadButton()} </x-flex>
        </x-flex>
        <x-flex horizontal space="md" wrap> ${this._getImageUploadComponent()} </x-flex>
        ${this._chancenBriefVariables()}
      </cr-flex>
    `;
  }

  /**
   * @returns Input fields for title, availability, min order amount, max order amount, quantity warning threshold, quantity warning mail.
   */
  private _basicInformationInputFields(): Renderable {
    return html`
      <cr-input ${this._form.field('title')} placeholder="Titel"></cr-input>
      <cr-dropdown-selection
        placeholder="Lieferbarkeit"
        .selectables=${Object.values(LogistikProductAvailability)}
        ${this._form.field('availability')}
      ></cr-dropdown-selection>
      <x-flex space="md" horizontal>
        <cr-input
          flex
          placeholder=${this.product.value instanceof WorkshopPack ? 'Mindestteilnehmerzahl' : 'Mindestbestellmenge'}
          type="number"
          ${this._form.field('minOrderAmount')}
        ></cr-input>
        <cr-input
          flex
          placeholder=${this.product.value instanceof WorkshopPack ? 'Maximalteilnehmerzahl ' : 'Maximalbestellmenge'}
          type="number"
          ${this._form.field('maxOrderAmount')}
        ></cr-input>
      </x-flex>
      <x-flex space="md" horizontal>
        <cr-input
          flex
          placeholder="Warnung bei weniger als"
          type="number"
          ${this._form.field('quantityWarningThreshold')}
        ></cr-input>
        <cr-input flex placeholder="Warnung per E-Mail an" ${this._form.field('quantityWarningMail')}></cr-input>
      </x-flex>
    `;
  }

  /**
   * @returns Input fields for product type specific information.
   */
  private _productSpecificInputFields(): Renderable {
    if (this.product.value instanceof LearningMaterial)
      return html` ${this._categoryGradeTopicInputFields()} ${this._solutionHandoutDigitalVariantInputFields()} `;
    if (this.product.value instanceof AdvertisingMaterial) return this._categoryTargetAudienceInputFields();
    if (this.product.value instanceof ChancenBrief || this.product.value instanceof TravelCertificate)
      return html`
        <cr-flex space="sm">
          <logistik-component-target-audience-selector
            ${this._form.field('targetAudiences')}
          ></logistik-component-target-audience-selector>
        </cr-flex>
      `;
    if (this.product.value instanceof LogistikProductSet)
      return html`
        <cr-flex space="sm">
          <logistik-component-product-set-item-selector ${this._form.field('selectedProducts')}>
          </logistik-component-product-set-item-selector>
          <cr-dropdown-multiselection
            ${this._form.field('services')}
            placeholder="Seiten"
            .selectables=${Object.values(Services)}
            .renderString=${(a: Services) => {
              switch (a) {
                case Services.Hefte:
                  return 'CHANCENhefte';
                case Services.Logistik:
                  return 'CHANCENlogistik';
              }
            }}
          >
          </cr-dropdown-multiselection>
        </cr-flex>
      `;
    return nothing;
  }

  /**
   * @returns Input fields for category and target audience.
   */
  private _categoryTargetAudienceInputFields(): Renderable {
    return html`
      <cr-flex horizontal space="sm">
        <cr-dropdown-selection
          ${css({ flexGrow: 1 })}
          ${this._form.field('category')}
          placeholder="Kategorie"
          .selectables=${Object.values(LogistikAdvertisingMaterialCategory)}
        >
        </cr-dropdown-selection>
        <logistik-component-target-audience-selector
          ${css({ flexGrow: 2 })}
          ${this._form.field('targetAudiences')}
        ></logistik-component-target-audience-selector>
      </cr-flex>
    `;
  }

  /**
   * @returns Input fields for categoory, grade, and topic.
   */
  private _categoryGradeTopicInputFields(): Renderable {
    return html`
      <cr-flex horizontal space="sm">
        <cr-dropdown-selection
          ${css({ flexGrow: 1 })}
          ${this._form.field('category')}
          placeholder="Fach"
          .selectables=${Object.values(LogistikLearningMaterialCategory)}
        >
        </cr-dropdown-selection>
        <cr-dropdown-multiselection
          ${css({ flexGrow: 1 })}
          ${this._form.field('grades')}
          placeholder="Jahrgangsstufen"
          .selectables=${Object.values(LogistikSchoolGrade)}
        >
        </cr-dropdown-multiselection>
        <cr-input ${css({ flexGrow: 1 })} ${this._form.field('topic')} placeholder="Thema"></cr-input>
      </cr-flex>
    `;
  }

  /**
   * @returns Input fields for solution, handout, and digital variant PDFs.
   */
  private _solutionHandoutDigitalVariantInputFields(): Renderable {
    return html`
      <cr-flex horizontal space="sm">
        <cr-file-input clearable flex placeholder="Lösungsheft" ${this._form.field('solutionPDFID')}></cr-file-input>
        <cr-file-input clearable flex placeholder="Handreichung" ${this._form.field('handoutPDFID')}></cr-file-input>
        <cr-file-input
          clearable
          flex
          placeholder="Digitale Variante"
          ${this._form.field('digitalVariantPDFID')}
        ></cr-file-input>
      </cr-flex>
    `;
  }

  override footerContent() {
    return html`
      <cr-flex horizontal wrap end space="sm">
        <logistik-component-delete-button
          @click="${async () => {
            if (!(await ChromiumDialog.confirm('Produkt wirklich löschen?'))) return;
            await this.product.value.remove();
            void this.close();
          }}"
        >
        </logistik-component-delete-button>
        <cr-button @click=${() => this._form.validate()} class="submit" flex> Speichern </cr-button>
      </cr-flex>
    `;
  }

  private _getImageUploadComponent() {
    return html`
      <x-multiple-image-picker ${this._form.field('imageIDs')} ${css({ overflowX: 'auto' })}> </x-multiple-image-picker>
    `;
  }

  private _getPDFUploadButton() {
    if (this.product.value instanceof LearningMaterial || this.product.value instanceof AdvertisingMaterial)
      return html`
        <cr-flex space="md">
          ${this._pdfID.value
            ? html` <cr-button
                mode="outline"
                @click=${async () => {
                  this._pdfID.next(null);
                }}
              >
                PDF-Vorschau entfernen
              </cr-button>`
            : html`
                <cr-button
                  mode="outline"
                  ?loading=${this._isUploadingPdf.value}
                  @click=${async () => {
                    this._isUploadingPdf.next(true);
                    const file = await askForFile({
                      acceptFileTypes: '.pdf',
                      acceptMultipleFiles: false,
                    }).catch(e => this._errorMsg.next(e.detail));
                    if (!file?.[0]) {
                      this._errorMsg.next('Beim Upload ist etwas schiefgegangen!');
                      this._isUploadingPdf.next(false);
                      return;
                    }
                    const fileID = await AdornisFile.upload(file[0]);
                    this._isUploadingPdf.next(false);
                    this._pdfID.next(fileID);
                  }}
                >
                  PDF-Vorschau hochladen
                </cr-button>
              `}
        </cr-flex>
      `;
  }

  private _getFilePreview() {
    if (!this._pdfFile.value || this._pdfFile.value === null) return nothing;
    return html`
      <cr-flex flex space="md" horizontal space-between crossaxis-center>
        <cr-flex>
          <cr-text>${this._pdfFile.value.meta?.fileName}</cr-text>
        </cr-flex>
        <cr-flex>
          <cr-button
            mode="icon"
            icon="download"
            @click=${() => goTo(this._pdfFile.value!.getDownloadLink())}
          ></cr-button>
        </cr-flex>
      </cr-flex>
    `;
  }

  private _chancenBriefVariables() {
    const product = this._selectedProductType.value;
    const form = this._form as unknown as FormController<ChancenBrief | TravelCertificate>;
    if (product instanceof ChancenBrief || product instanceof TravelCertificate)
      return html`
        <cr-text tone="subtle">
          Variablen im Word-Dokument werden mit geschweiften Klammern definiert (z.B. {Vorname}). QR-Codes werden
          zusätzlich mit einem '%' definiert (z.B. {%qrcode}).
        </cr-text>
        <cr-file-input
          placeholder="CHANCENbrief Word Template hochladen"
          ${form.field('flyaDocumentID')}
        ></cr-file-input>
        <x-flex space="md">
          <x-flex horizontal crossaxis-center space="sm">
            <cr-h4> CHANCENbrief Variablen definieren </cr-h4>
            <cr-icon> info </cr-icon>
            <cr-tooltip
              .text=${html`
                <x-flex space="md">
                  <cr-text> Folgende Variablen können beim Bestellprozess automatisch vorausgefüllt werden: </cr-text>
                  <ul>
                    <x-flex space="sm">
                      ${possiblePrefillChancenBriefVariableNames.map(v => html` <li><cr-text> ${v} </cr-text></li> `)}
                    </x-flex>
                  </ul>
                </x-flex>
              `}
            ></cr-tooltip>
          </x-flex>
          <x-flex space="sm">
            ${repeat(
              product.flyaVariables ?? [],
              chancenBriefVariable => chancenBriefVariable._id,
              (chancenBriefVariable, i) => html`
                <x-flex horizontal crossaxis-center space="sm">
                  <cr-input
                    flex
                    placeholder="Platzhalter-Name"
                    ${form.field('flyaVariables', i, 'variableName')}
                  ></cr-input>
                  ${chancenBriefVariable.type === DocxVariableType.QR_CODE
                    ? html`
                        <cr-input
                          placeholder="Breite (in Pixel)"
                          ${form.field('flyaVariables', i, 'width')}
                          type="number"
                        ></cr-input>
                        <cr-input
                          placeholder="Höhe (in Pixel)"
                          ${form.field('flyaVariables', i, 'height')}
                          type="number"
                        ></cr-input>
                      `
                    : nothing}
                  <cr-dropdown-selection
                    placeholder="Platzhaltertyp"
                    .selectables=${Object.values(DocxVariableType)}
                    ${form.field('flyaVariables', i, 'type')}
                    ${css({ minWidth: '145px' })}
                  ></cr-dropdown-selection>
                  <cr-button
                    mode="icon"
                    icon="delete"
                    tone="error"
                    @click=${() => {
                      if (!product.flyaVariables || !product.flyaVariables.length) return;
                      product.flyaVariables.splice(i, 1);
                      this.requestUpdate();
                    }}
                  >
                  </cr-button>
                </x-flex>
              `,
            )}
            <cr-button
              mode="outline"
              icon="add"
              @click=${() => {
                if (!product.flyaVariables) product.flyaVariables = [];
                product.flyaVariables.push(new ChancenBriefVariable({ _id: A.getGloballyUniqueID() }));
                this.requestUpdate();
              }}
            >
              Weiteren Platzhalter hinzufügen
            </cr-button>
          </x-flex>
        </x-flex>
      `;
    return nothing;
  }

  public override async cancel() {
    if (this._productDirty.value && this.product.value._id) {
      if (!(await ChromiumDialog.confirm('Ungespeicherte Änderungen verwerfen?'))) return;
      if (this.product.value._id) this.product.value.revert();
    }
    super.cancel();
  }

  public override getInnerWrapper(options: DialogOptions) {
    const wrapperElem = super.getInnerWrapper(options as Required<DialogOptions>);

    if (!options.draggable) {
      wrapperElem.style.setProperty('max-width', '60vw');
      wrapperElem.style.setProperty('max-height', '100vh');
      wrapperElem.style.setProperty('min-width', '60vw');
      // wrapperElem.style.setProperty('min-height', '60vh');
    }
    return wrapperElem;
  }
}
